import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, IconButton, Modal, Spinner, Tooltip } from '@volvo/vcdk-react';
import copy from 'copy-to-clipboard';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { IFleetInvitation } from '@/service/vpc/types/IFleetInvitations';
import type { ColumnDef } from '@tanstack/react-table';

import EmptyState from '@/components/EmptyState';
import FormatDateTime from '@/components/FormatDateTime';
import Table from '@/components/Table';
import { analyticsEvents } from '@/contexts/analytics/events';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import useMediaQuery from '@/hooks/useMediaQuery';
import { getFleetInvitationsQueryOptions } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';

const InviteMembersModal = () => {
  const { t } = useTranslation();
  const { fleetAccountId } = useFleetAccount();
  const { mediaQueryNodes: mq } = useMediaQuery();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { data, isLoading, refetch } = useQuery({
    ...getFleetInvitationsQueryOptions({
      fleetAccountId: fleetAccountId!,
      status: 'Issued',
      pageNumber: 1,
      pageSize: 10,
    }),
    enabled: isOpen,
  });

  const { mutate: generateInvitation, isPending } = useMutation({
    mutationFn: (fleetAccountId: string) => vpcService.Invitations.createInvitation({ fleetAccountId }),
    onSuccess: () => {
      refetch();
    },
  });

  const { mutate: cancelInvitation, isPending: isCancelling } = useMutation({
    mutationFn: ({ fleetAccountId, invitationId }: { fleetAccountId: string; invitationId: string }) =>
      vpcService.Invitations.cancelInvitation({ fleetAccountId, invitationId }),
    onSuccess: () => {
      refetch();
    },
  });

  const mqSize = mq({
    default: 'mobile',
    md: 'desktop',
  });

  const columns = [
    {
      accessorFn: (row) => row.issuedAt,
      id: 'issuedAt',
      header: t(($) => $.components.table.invitations_table.creation_date),
      cell: (info) => {
        const issuedAt = info.getValue<string>();
        return <FormatDateTime date={issuedAt} type="datetime" variant="body" className="text-subtle" />;
      },
    },
    {
      accessorKey: 'actions',
      id: 'actions',
      header: '',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex gap-2 md:justify-end">
            <CopyButton mqSize={mqSize} invitationId={row.invitationId} />
            {mqSize === 'mobile' ? (
              <Button
                variant="secondary"
                disabled={isLoading || isCancelling}
                className="w-full"
                onClick={() => {
                  analyticsEvents.Interaction.componentInteraction({
                    parent: 'InviteMembersModal',
                    type: 'Button',
                    title: 'Cancel Invitation',
                    placement: 'InviteMembersModal',
                  });
                  cancelInvitation({
                    fleetAccountId: fleetAccountId!,
                    invitationId: row.invitationId,
                  });
                }}
              >
                {t(($) => $.common.delete)}
              </Button>
            ) : (
              <IconButton
                icon="trash"
                size="medium"
                disabled={isLoading || isCancelling}
                onClick={() =>
                  cancelInvitation({
                    fleetAccountId: fleetAccountId!,
                    invitationId: row.invitationId,
                  })
                }
              />
            )}
          </div>
        );
      },
    },
  ] as ColumnDef<IFleetInvitation>[];

  return (
    <>
      <Button
        variant="primary"
        size="medium"
        onClick={() => {
          analyticsEvents.Interaction.componentInteraction({
            parent: 'InviteMembersModal',
            type: 'Button',
            title: 'Open Invite Members Modal',
            placement: 'InviteMembersModal',
          });
          setIsOpen(true);
        }}
      >
        {t(($) => $.components.modal.invite_members_modal.button)}
      </Button>
      {isOpen &&
        createPortal(
          <Modal open={isOpen} onClose={() => setIsOpen(false)} width={700} maxWidth="90%">
            <span slot="header">{t(($) => $.components.modal.invite_members_modal.title)}</span>
            <div className="mb-6">
              {isLoading ? (
                <div className="border-subtle bg-neutral-light flex items-center justify-center rounded border p-6 py-12">
                  <Spinner size="medium" />
                </div>
              ) : data?.totalItems === 0 ? (
                <div className="bg-base border-subtle rounded border p-6">
                  <EmptyState
                    title={t(($) => $.components.modal.invite_members_modal.empty_state.title)}
                    message={t(($) => $.components.modal.invite_members_modal.empty_state.message)}
                  />
                </div>
              ) : (
                <Table
                  key={mqSize}
                  isLoading={isLoading}
                  columns={columns}
                  data={data?.items || []}
                  showHeader={false}
                  showSearch={false}
                />
              )}
            </div>
            <span slot="modalFooter">
              <Button
                iconEnd="link"
                variant="primary"
                size="medium"
                loading={isPending}
                onClick={() => {
                  analyticsEvents.Interaction.componentInteraction({
                    parent: 'InviteMembersModal',
                    type: 'Button',
                    title: 'Generate Invitation Link',
                    placement: 'InviteMembersModal',
                  });
                  generateInvitation(fleetAccountId!);
                }}
                className="w-full"
              >
                {t(($) => $.components.modal.invite_members_modal.generate_invitation_link)}
              </Button>
            </span>
          </Modal>,
          document.body
        )}
    </>
  );
};

const CopyButton: React.FC<{ mqSize: string; invitationId: string }> = ({ mqSize, invitationId }) => {
  const { t } = useTranslation();
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null);
  const [openTooltip, setOpenTooltip] = useState(false);

  const handleOnClick = (invitationId: string) => {
    const { origin, hostname } = window.location;
    const useBasePath = hostname !== 'localhost' && hostname !== '127.0.0.1';

    // https://rc.volvopenta.com/connect/invite/aa243eb6-8c29-4a05-9909-d8beff3810f9
    // http://localhost:3000/invite/9f44c4dd-e4cd-405d-9f82-21929d4c6c76
    const base = useBasePath ? '/connect/' : '/';
    const url = `${origin}${base}invite/${invitationId}`.replace(/([^:])\/\//g, '$1/');
    copy(url);

    setOpenTooltip(true);
    setTimeout(() => {
      setOpenTooltip(false);
    }, 2000);
  };

  return (
    <div className="">
      <Tooltip
        reference={referenceElement}
        placement={'top'}
        offset={10}
        id="tooltip-1"
        referenceTrigger="none"
        open={openTooltip}
      >
        {t(($) => $.components.tooltip.copy_clipboard)}
      </Tooltip>
      <div className="flex justify-between">
        {mqSize === 'mobile' ? (
          <Button variant="primary" onClick={() => handleOnClick(invitationId)} className="w-full">
            {t(($) => $.components.tooltip.copy_clipboard)}
          </Button>
        ) : (
          <IconButton
            ref={(ref) => setReferenceElement(ref)}
            aria-describedby="tooltip-1"
            icon="copy"
            onClick={() => handleOnClick(invitationId)}
          />
        )}
      </div>
    </div>
  );
};

export default InviteMembersModal;
